'use client';

import React, { createContext, useContext, useMemo } from 'react';
import {
  convertAmount,
  formatEquivalents,
  formatMoney,
  normalizeCurrency,
  type CurrencyCode,
  type FxRates,
} from '@/lib/currency';

interface CurrencyCtx {
  /** Agency (transaction) currency. */
  code: CurrencyCode;
  /** Visitor display currency (header selector). Falls back to agency code. */
  display: CurrencyCode;
  rates: FxRates;
  /** Amount in agency currency → formatted in DISPLAY currency. */
  fmt: (n: number) => string;
  /** Tourist hint in the other two currencies. */
  equiv: (n: number) => string;
}

const CurrencyContext = createContext<CurrencyCtx>({
  code: 'MAD',
  display: 'MAD',
  rates: { rateEur: 10.8, rateUsd: 10.0 },
  fmt: (n) => formatMoney(n, 'MAD'),
  equiv: () => '',
});

export function CurrencyProvider({
  code,
  display,
  rates,
  showEquivalents = true,
  children,
}: {
  code?: string | null;
  /** Null/undefined = follow the agency currency. */
  display?: string | null;
  rates?: FxRates | null;
  showEquivalents?: boolean;
  children: React.ReactNode;
}) {
  const value = useMemo<CurrencyCtx>(() => {
    const c = normalizeCurrency(code);
    const d = display ? normalizeCurrency(display) : c;
    const r = {
      rateEur: rates?.rateEur && rates.rateEur > 0 ? rates.rateEur : 10.8,
      rateUsd: rates?.rateUsd && rates.rateUsd > 0 ? rates.rateUsd : 10.0,
    };
    return {
      code: c,
      display: d,
      rates: r,
      fmt: (n: number) => formatMoney(convertAmount(n, c, d, r), d),
      equiv: (n: number) => {
        if (!showEquivalents) return '';
        const others = (['MAD', 'EUR', 'USD'] as CurrencyCode[])
          .filter((k) => k !== d)
          .map((k) => formatMoney(convertAmount(n, c, k, r), k));
        return `≈ ${others.join(' · ')}`;
      },
    };
  }, [code, display, rates?.rateEur, rates?.rateUsd, showEquivalents]);
  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

/** Website-wide currency. Defaults to MAD. */
export function useCurrency(): CurrencyCtx {
  return useContext(CurrencyContext);
}
